const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04]
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46]
const OLE2_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]

const OLE2_SCAN_CAP_BYTES = 4 * 1024 * 1024
const PDF_TRAILER_SCAN_BYTES = 32 * 1024
const PDF_ENCRYPT_MARKER = '/Encrypt'

function encodeUtf16Le(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length * 2)
  for (let index = 0; index < text.length; index += 1) {
    const codeUnit = text.charCodeAt(index)
    bytes[index * 2] = codeUnit & 0xff
    bytes[index * 2 + 1] = (codeUnit >> 8) & 0xff
  }
  return bytes
}

const DRM_ENCRYPTED_TRANSFORM_MARKER = encodeUtf16Le('DRMEncryptedTransform')
const ENCRYPTED_PACKAGE_MARKER = encodeUtf16Le('EncryptedPackage')
const STRONG_ENCRYPTION_MARKER = encodeUtf16Le('StrongEncryptionDataSpace')

export type UnreadableFileContainerDetection =
  | { kind: 'readable' }
  | { kind: 'rights-protected' }
  | { kind: 'encrypted' }
  | { kind: 'invalid-pdf' }

export class PackagingDocumentUnreadableUploadError extends Error {
  readonly reason: 'rights-protected' | 'encrypted' | 'invalid-pdf'

  constructor(reason: 'rights-protected' | 'encrypted' | 'invalid-pdf') {
    super('Packaging document upload blocked: unreadable file container')
    this.name = 'PackagingDocumentUnreadableUploadError'
    this.reason = reason
  }
}

export function isPackagingDocumentUnreadableUploadError(
  error: unknown
): error is PackagingDocumentUnreadableUploadError {
  return error instanceof PackagingDocumentUnreadableUploadError
}

export class PackagingDocumentInvalidUploadError extends Error {
  readonly reason: 'empty' | 'unsupported-type'

  constructor(reason: 'empty' | 'unsupported-type') {
    super('Packaging document upload blocked: invalid file')
    this.name = 'PackagingDocumentInvalidUploadError'
    this.reason = reason
  }
}

export function isPackagingDocumentInvalidUploadError(
  error: unknown
): error is PackagingDocumentInvalidUploadError {
  return error instanceof PackagingDocumentInvalidUploadError
}

export function resolvePackagingDocumentUploadErrorMessage(
  error: unknown,
  messages: {
    uploadFailed: string
    uploadRightsProtected: string
    uploadEncrypted: string
    uploadInvalidPdf?: string
    uploadFileTooLarge?: string
    uploadUnsupportedFileType?: string
    uploadEmptyFile?: string
  }
): string {
  if (
    error != null &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'UploadTooLargeError' &&
    messages.uploadFileTooLarge
  ) {
    return messages.uploadFileTooLarge
  }
  if (isPackagingDocumentInvalidUploadError(error)) {
    if (error.reason === 'empty' && messages.uploadEmptyFile) {
      return messages.uploadEmptyFile
    }
    if (error.reason === 'unsupported-type' && messages.uploadUnsupportedFileType) {
      return messages.uploadUnsupportedFileType
    }
  }
  if (isPackagingDocumentUnreadableUploadError(error)) {
    if (error.reason === 'invalid-pdf' && messages.uploadInvalidPdf) {
      return messages.uploadInvalidPdf
    }
    if (error.reason === 'rights-protected') {
      return messages.uploadRightsProtected
    }
    return messages.uploadEncrypted
  }
  return messages.uploadFailed
}

function startsWithMagic(buffer: Uint8Array, magic: number[]): boolean {
  if (buffer.length < magic.length) {
    return false
  }
  for (let index = 0; index < magic.length; index += 1) {
    if (buffer[index] !== magic[index]) {
      return false
    }
  }
  return true
}

function containsSubarray(buffer: Uint8Array, pattern: Uint8Array): boolean {
  if (pattern.length === 0 || buffer.length < pattern.length) {
    return false
  }
  const lastStart = buffer.length - pattern.length
  for (let start = 0; start <= lastStart; start += 1) {
    let matched = true
    for (let index = 0; index < pattern.length; index += 1) {
      if (buffer[start + index] !== pattern[index]) {
        matched = false
        break
      }
    }
    if (matched) {
      return true
    }
  }
  return false
}

function containsAsciiMarker(buffer: Uint8Array, marker: string): boolean {
  const pattern = new TextEncoder().encode(marker)
  return containsSubarray(buffer, pattern)
}

function startsWithAsciiPrefix(bytes: Uint8Array, prefix: string): boolean {
  if (bytes.length < prefix.length) {
    return false
  }
  for (let index = 0; index < prefix.length; index += 1) {
    if (bytes[index] !== prefix.charCodeAt(index)) {
      return false
    }
  }
  return true
}

function fileNameClaimsPdf(fileName: string): boolean {
  return fileName.toLowerCase().trim().endsWith('.pdf')
}

async function readFilePrefix(file: File | Blob, length: number): Promise<Uint8Array> {
  const prefixSlice = file.slice(0, Math.min(file.size, length))
  return new Uint8Array(await prefixSlice.arrayBuffer())
}

async function detectPdfEncryption(file: File | Blob): Promise<boolean> {
  const scanLength = Math.min(file.size, PDF_TRAILER_SCAN_BYTES)
  if (scanLength === 0) {
    return false
  }
  const trailerSlice = file.slice(Math.max(0, file.size - scanLength), file.size)
  const trailer = new Uint8Array(await trailerSlice.arrayBuffer())
  // Password-protected PDFs declare an /Encrypt dictionary in the trailer/xref area.
  // Scanning the last 32KB catches typical trailers without reading the whole file.
  return containsAsciiMarker(trailer, PDF_ENCRYPT_MARKER)
}

export async function detectUnreadableFileContainer(
  file: File | Blob
): Promise<UnreadableFileContainerDetection> {
  const prefix = await readFilePrefix(file, 32)
  const header = prefix.subarray(0, 8)

  if (
    startsWithAsciiPrefix(prefix, 'HSKey') ||
    startsWithAsciiPrefix(prefix, '%TSD-Header')
  ) {
    return { kind: 'rights-protected' }
  }

  const fileName = file instanceof File ? file.name : undefined
  if (
    fileName != null &&
    fileNameClaimsPdf(fileName) &&
    !startsWithMagic(header, PDF_MAGIC)
  ) {
    return { kind: 'invalid-pdf' }
  }

  if (startsWithMagic(header, ZIP_MAGIC)) {
    return { kind: 'readable' }
  }

  if (startsWithMagic(header, PDF_MAGIC)) {
    const isEncrypted = await detectPdfEncryption(file)
    if (isEncrypted) {
      return { kind: 'encrypted' }
    }
    return { kind: 'readable' }
  }

  if (!startsWithMagic(header, OLE2_MAGIC)) {
    return { kind: 'readable' }
  }

  const scanLength = Math.min(file.size, OLE2_SCAN_CAP_BYTES)
  const bodySlice = file.slice(0, scanLength)
  const body = new Uint8Array(await bodySlice.arrayBuffer())

  const hasRightsProtectionMarker =
    containsSubarray(body, DRM_ENCRYPTED_TRANSFORM_MARKER) ||
    containsSubarray(body, ENCRYPTED_PACKAGE_MARKER)
  if (hasRightsProtectionMarker) {
    return { kind: 'rights-protected' }
  }

  if (containsSubarray(body, STRONG_ENCRYPTION_MARKER)) {
    return { kind: 'encrypted' }
  }

  return { kind: 'readable' }
}

export async function assertPackagingDocumentFileReadable(file: File): Promise<void> {
  const detection = await detectUnreadableFileContainer(file)
  if (detection.kind === 'invalid-pdf') {
    throw new PackagingDocumentUnreadableUploadError('invalid-pdf')
  }
  if (detection.kind === 'rights-protected') {
    throw new PackagingDocumentUnreadableUploadError('rights-protected')
  }
  if (detection.kind === 'encrypted') {
    throw new PackagingDocumentUnreadableUploadError('encrypted')
  }
}
